import * as React from "react";
import {FormControl, FormControlLabel, Radio, RadioGroup} from "@mui/material";
import {useTranslation} from "react-i18next";
import {DeclineReason} from "@/api/data-contracts";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {DeclineReasonI18N} from "@/translations";
import {
    DECLINE_REASON_LABEL_KEYS,
    isFreeTextMandatory,
    SELECTABLE_DECLINE_REASONS
} from "@/common/request-cards/decline-reason-logic";

/**
 * Props for the shared decline-reason selector — a controlled presentation component owning no state.
 * The parent holds the selected reason and free-text value and receives changes back.
 */
interface DeclineReasonSelectProps {
    reason: DeclineReason | undefined;
    freeText: string | null;
    onReasonChange: (reason: DeclineReason) => void;
    onFreeTextChange: (freeText: string) => void;
    /** Unique field name for the free-text input, so several selectors can coexist on one page. */
    freeTextFieldName: string;
}

/**
 * Shared decline-reason selector reused across the post-login feedback dialog and the manual
 * quote-decline dialog: a RadioGroup over the {@link SELECTABLE_DECLINE_REASONS} (including
 * NO_REASON_GIVEN as a regular, directly selectable option, but excluding the system-only
 * AWARDED_TO_OTHER_SUPPLIER) plus a free-text field whose label switches dynamically between
 * "(optional)" and "(Pflichtfeld)" depending on whether the selected reason mandates free text (via
 * {@link isFreeTextMandatory}).
 */
const DeclineReasonSelect: React.FC<DeclineReasonSelectProps> = ({
    reason,
    freeText,
    onReasonChange,
    onFreeTextChange,
    freeTextFieldName
}) => {
    const {t} = useTranslation();

    const freeTextLabelKey = isFreeTextMandatory(reason)
        ? DeclineReasonI18N.freeTextLabelMandatory
        : DeclineReasonI18N.freeTextLabelOptional;

    return (
        <>
            <FormControl component="fieldset">
                <RadioGroup value={reason ?? ""} onChange={(e) => onReasonChange(e.target.value as DeclineReason)}>
                    {SELECTABLE_DECLINE_REASONS.map((value) => (
                        <FormControlLabel
                            key={value}
                            value={value}
                            control={<Radio />}
                            label={t(DECLINE_REASON_LABEL_KEYS[value])}
                        />
                    ))}
                </RadioGroup>
            </FormControl>
            <StringInputField
                label={t(freeTextLabelKey)}
                name={freeTextFieldName}
                value={freeText}
                onChange={(e) => onFreeTextChange(e.target.value)}
                multiline
                minRows={2}
            />
        </>
    );
};

export default DeclineReasonSelect;
